import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { SvcComment, SvcCommentAttachment, SvcCommentAttachmentType } from '../../models/comment.model';
import { ISvcUserAvatar, UserAvatarSize } from '@solvace/component/svc-user-avatar';
import { SvcCommentsChatComponent } from '../svc-comments-chat/svc-comments-chat.component';
import { SvcCommentsService } from '../../services/svc-comments.service';
import { catchError, finalize, fromEvent, Subject, takeUntil, tap } from 'rxjs';
import { getCdkScrollableElement, getMatDialogScrollableElement, HttpErrorService, isInCdkOrMatDialog } from 'projects/lib-shared-common/src/public-api';
import { SvcAppSettings, SvcModule } from 'projects/lib-shared-common/src/public-api';
import { SvcTotalReactionsComponent } from '../../../svc-reaction/svc-total-reactions/svc-total-reactions.component';
import { SvcMediaCarouselItem, SvcMediaCarouselRenderMode } from '@solvace/component/svc-media-carousel';

@Component({
  selector: 'svc-comments-message',
  templateUrl: './svc-comments-message.component.html',
  styleUrls: ['./svc-comments-message.component.scss'],
  providers: [SvcCommentsService],
})
export class SvcCommentsMessageComponent implements AfterViewInit, OnInit, OnDestroy, OnChanges {
  @Input() public message: SvcComment;
  @Input() public configTypeId: string;
  @Input() public parentMessage: SvcComment;
  @Input() public smallSize: boolean = false;
  @Input() public applicationId: string;
  @Input() public siteId: number;
  @Input() public external: boolean = false;
  @Input() public parentApplicationId: string;
  @Input() public parentRegisterId: number | string;
  @Input() public parentRegisterUId?: string;
  @Input() public parentOriginatorId?: string;
  @Input() public parentRegisterCode?: number;
  @Input() public parentRegistryCode?: number;
  @Input() public fromFeed = false;

  @Output() public onCommentRemoved = new EventEmitter<SvcComment>();
  @Output() public onCommentAdded = new EventEmitter<SvcComment>();

  @ViewChild('svcCommentsChatComponent') public svcCommentsChatComponent!: SvcCommentsChatComponent;
  @ViewChild('svcTotalReactions') public svcTotalReactions!: SvcTotalReactionsComponent;

  public AttachmentType = SvcCommentAttachmentType;
    public readonly mediaCarouselRenderMode = SvcMediaCarouselRenderMode.ONE_BY_ONE;
  public isChild: boolean = false;
  public userAvatar: ISvcUserAvatar;
  public mediasCarousel: SvcMediaCarouselItem[];
  public attachments: SvcCommentAttachment[];
  public showChildrenMessages: boolean = false;
  public hasImageOrAudioAttachment: boolean = false;
  public removingIsLoading: boolean = false;
  public translatingIsLoading: boolean = false;
  public fromHome = this._svcAppSettings.module === SvcModule.MyWorkspace;
  public reactionRelativeElementClass: (string | HTMLElement)[];

  #destroy$ = new Subject<void>();

  constructor(
    private _commentsService: SvcCommentsService,
    private _httpErrorService: HttpErrorService,
    private _svcAppSettings: SvcAppSettings,
    private _elementRef: ElementRef<HTMLElement>,
    private _cdr: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this.isChild = !!this.parentMessage;

    this.message.commentText = this.handlerText(this.message.commentText);

    this.userAvatar = {
      name: this.message.user.name,
      initialsColor: this.message.user.color,
      picture: this.message.user.picture,
      size: UserAvatarSize.Comment,
    };
    this.mediasCarousel = this.message.attachments
      .filter((item) => {
        return [SvcCommentAttachmentType.IMAGE, SvcCommentAttachmentType.VIDEO].includes(item.type);
      })
      .map((item) => ({
        name: item.name,
        url: item.assignUrl,
        type: item.type === SvcCommentAttachmentType.VIDEO ? 'video' : 'image',
      }));
    this.attachments = this.message.attachments.filter((item) => !this.mediasCarousel.some((x) => x.url === item.assignUrl));
    this.hasImageOrAudioAttachment = this.message.attachments.some((x) => [SvcCommentAttachmentType.AUDIO, SvcCommentAttachmentType.IMAGE].includes(x.type));
  }

  public ngOnChanges(): void {
    this.ngOnInit();
  }

  public ngAfterViewInit(): void {
    this.#setReactionRelativeElementClass();
  }

  public openAttachment(attachment: SvcCommentAttachment): void {
    if (!this.removingIsLoading && !this.translatingIsLoading) {
      const link = document.createElement('a');
      link.target = '_blank';
      link.href = attachment.assignUrl;
      link.click();
    }
  }

  public async answer(options?: { force: boolean; focus: boolean }): Promise<void> {
    if (options?.force || (!this.removingIsLoading && !this.translatingIsLoading)) {
      await this.showMessages({ showInput: true, inputFocus: options?.focus });
    }
  }

  public showMessages(options?: { showInput?: boolean; inputFocus?: boolean }): Promise<void> {
    const finalize = (resolve: () => void): void => {
      if (options?.inputFocus) {
        this.svcCommentsChatComponent.focus();
      } else if (options?.showInput) {
        this.svcCommentsChatComponent.showInput = true;
      }
      this.svcCommentsChatComponent.loadSuggestions();
      this._cdr.markForCheck();
      resolve();
    };
    return new Promise<void>((resolve) => {
      if (this.showChildrenMessages) {
        finalize(resolve);
        return;
      }
      this.showChildrenMessages = true;
      setTimeout(() => {
        finalize(resolve);
      });
    });
  }

  public translate(): void {
    if (!this.removingIsLoading && !this.translatingIsLoading) {
      this.translatingIsLoading = true;
      this._commentsService
        .translateTextComment(this.message.commentText)
        .pipe(
          tap((text: string) => {
            this.message.translatedCommentText = text?.replace('bg-primary-100', '');
            this.message.translated = true;
            this._cdr.markForCheck();
          }),
          catchError((error) => {
            this._httpErrorService.showErrorInToast(error);
            this._cdr.markForCheck();
            return error;
          }),
          finalize(() => {
            this.translatingIsLoading = false;
            this._cdr.markForCheck();
          })
        )
        .subscribe();
    }
  }

  public exclude(): void {
    if (!this.removingIsLoading && !this.translatingIsLoading) {
      this.removingIsLoading = true;
      this._commentsService
        .removeComment(this.message.commentId)
        .pipe(
          tap(() => {
            this.onCommentRemoved.emit(this.message);
          }),
          catchError((error) => {
            this._httpErrorService.showErrorInToast(error);
            this._cdr.markForCheck();
            return error;
          }),
          finalize(() => {
            this.removingIsLoading = false;
            this._cdr.markForCheck();
          })
        )
        .subscribe();
    }
  }

  public handlerText(text: string): string {
    return text?.replace(/(?<=class\s*=\s*["'][^"']*)\b(mention|feed-mention)\b/g, 'bg-primary-100 text-primary') || '';
  }

  #setReactionRelativeElementClass(): void {
    const parentChatElement = this.#getParentChatElement();
    if (!this.fromFeed) {
      let elements: (string | HTMLElement)[] = [];
      if (isInCdkOrMatDialog(this._elementRef?.nativeElement)) {
        const checkElement = (): void => {
          this.reactionRelativeElementClass = [
            parentChatElement,
            getCdkScrollableElement(),
            getMatDialogScrollableElement(),
            'svc-dialog .overflow-auto',
            'svc-dialog-primary .overflow-auto',
          ];
        };
        fromEvent(window, 'resize')
          .pipe(
            takeUntil(this.#destroy$),
            tap(() => checkElement())
          )
          .subscribe();
        checkElement();
        return;
      } else if (!this.fromHome) {
        elements = ['.svc-custom-layout-content .svc-drawer-content'];
        if (!document.querySelector(elements[0] as string)) {
          elements = ['.svc-custom-layout-content'];
        }
      } else {
        elements = ['.mat-mdc-tab-nav-panel'];
      }
      this.reactionRelativeElementClass = [parentChatElement, ...elements];
    } else {
      this.reactionRelativeElementClass = [];
    }
  }

  #getParentChatElement(): HTMLElement {
    let searchingTheFirst = true;
    const parentChatTagName = 'SVC-COMMENTS-CHAT';
    const needsTakeTheNextChat = !!this.parentMessage;
    const keepGoing = (tagName: string): boolean => {
      if (!['BODY', parentChatTagName].includes(tagName)) return true;
      if (tagName === parentChatTagName && needsTakeTheNextChat && searchingTheFirst) {
        searchingTheFirst = false;
        return true;
      }
      return false;
    };
    let parentElement = this._elementRef?.nativeElement?.parentElement;
    while (keepGoing(parentElement?.tagName)) {
      parentElement = parentElement.parentElement;
    }
    return parentElement?.tagName === parentChatTagName ? parentElement : null;
  }

  public ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
